/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import {
  Grid,
  Form,
  DateInput,
  Button,
  Icon,
  Modal,
  Tooltip,
} from '@mairistem/composants';

import { SectionHeader } from '@mairistem/administres-utils';

import { ModalRegistreTreeView } from '@/components/Common';
import { API_DATE_FORMAT, URL_SOUS_TYPES_ACTE_NAISSANCE } from '@/constants';
import { useOptionsReferentiel } from '@/hooks';
import {
  createTimeInputHandler,
  buildRegistreActeUpdatePayload,
} from '@/utils';

interface ModalRegistreActeProps {
  open: boolean;
  acte?: any;
  /** Instance `service` de `useFiche` fournie par `ActeFicheContainer` (celle de la lecture). */
  service?: { update: (data: Record<string, any>) => Promise<any> };
  /** Référentiel du sous-type d'acte (défaut : sous-types de naissance). */
  urlSousTypesActe?: string;
  /** Types de registre proposés par l'arbre de sélection (défaut : naissance). */
  typeRegistre?: string;
  /**
   * Champ où écrire le libellé du sous-type choisi. La naissance l'affiche
   * depuis `libelleTypeActe` ; la reconnaissance depuis `libelleSousTypeActe`,
   * son `libelleTypeActe` valant toujours la constante « Reconnaissance ».
   */
  champLibelleSousType?: 'libelleTypeActe' | 'libelleSousTypeActe';
  /**
   * Ajoute le sous-bloc « Officier d'état civil » (nom, prénom, grade), propre
   * au MARIAGE : `ec_actm` est la seule table à porter ces colonnes, et sa
   * maquette les range dans ce panneau.
   *
   * Absent (défaut), le formulaire est celui des trois autres types d'acte.
   */
  champsOfficier?: boolean;
  onClose: () => void;
  onSaved: (acte: any) => void;
}

const CHAMPS_EDITABLES = [
  'registre',
  'identifiantSousTypeActe',
  'libelleTypeActe',
  'libelleSousTypeActe',
  'dateDresseActe',
  'heureDresseActe',
  'minuteDresseActe',
  'numeroActe',
  'numeroOrdre',
  'acteNumeroComplement',
  /*
   * Officier d'état civil, propre au mariage. Les nommer ici est sans effet sur
   * les autres types d'acte : `_.pick` ne retient que ce que l'acte porte, et le
   * PUT ne reçoit donc jamais de clé inconnue de sa table.
   */
  'acteSignataireNomFamille',
  'acteSignatairePrenoms',
  'acteSignataireQualite',
];

/** `numeroActe`/`numeroOrdre` n'acceptent que des chiffres, filtrés au fil de la saisie. */
const CHAMPS_NUMERIQUES = ['numeroActe', 'numeroOrdre'];

const ModalRegistreActe = (props: ModalRegistreActeProps) => {
  const {
    open,
    acte,
    service,
    urlSousTypesActe = URL_SOUS_TYPES_ACTE_NAISSANCE,
    typeRegistre,
    champsOfficier = false,
    champLibelleSousType = 'libelleTypeActe',
    onClose,
    onSaved,
  } = props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);
  const [openModalRegistre, setOpenModalRegistre] = React.useState(false);

  const { options: sousTypeActeOptions, items: sousTypesActe } =
    useOptionsReferentiel(open ? urlSousTypesActe : undefined);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData(_.pick(acte, CHAMPS_EDITABLES));
    setAttempted(false);
  }, [open, acte]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      const finalValue = CHAMPS_NUMERIQUES.includes(name)
        ? String(value).replace(/[^0-9]/g, '')
        : value;
      setData((prev) => ({ ...prev, [name]: finalValue }));
    },
    [],
  );

  const handleSousTypeChange = React.useCallback(
    (_event: React.SyntheticEvent | null, payload: { value: unknown }) => {
      const item = _.find(sousTypesActe, { identifiant: payload.value });
      setData((prev) => ({
        ...prev,
        identifiantSousTypeActe: payload.value,
        [champLibelleSousType]: item?.libelle,
      }));
    },
    [sousTypesActe, champLibelleSousType],
  );

  const handleTimeInput = React.useMemo(
    () => createTimeInputHandler(handleChange),
    [handleChange],
  );

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      setData((prev) => ({
        ...prev,
        dateDresseActe: _.isNil(date)
          ? null
          : dayjs(date).format(API_DATE_FORMAT),
      }));
    },
    [],
  );

  const handleSelectRegistre = React.useCallback(
    (registre: Record<string, any>) => {
      setData((prev) => ({ ...prev, registre }));
    },
    [],
  );

  /** Mêmes règles que `validationInformationsActe` (assistant) */
  const errors = React.useMemo(() => {
    const e: Record<string, string> = {};
    if (_.isEmpty(data?.registre)) {
      e.registre = 'Le registre est obligatoire';
    }
    if (
      _.isNil(data?.identifiantSousTypeActe) ||
      data?.identifiantSousTypeActe === ''
    ) {
      e.identifiantSousTypeActe = "Le sous-type d'acte est obligatoire";
    }
    if (_.isEmpty(data?.dateDresseActe)) {
      e.dateDresseActe = "La date de l'acte est obligatoire";
    }
    if (_.isEmpty(_.toString(data?.numeroActe))) {
      e.numeroActe = 'Le numéro est obligatoire';
    }
    return e;
  }, [data]);

  const displayedErrors = attempted ? errors : {};

  const handleSave = React.useCallback(() => {
    setAttempted(true);
    if (!_.isEmpty(errors)) {
      return;
    }

    setSaving(true);
    service
      ?.update(buildRegistreActeUpdatePayload(acte?.identifiant, data))
      .then(() => {
        onSaved({ ...acte, ...data });
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de l'acte",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [errors, service, acte, data, onSaved, onClose]);

  return (
    <>
      <Modal open={open} onClose={onClose} size="medium">
        <Modal.Header content="Modifier le registre et l'acte" />
        <Modal.Content>
          {/* @ts-ignore */}
          <Form>
            <Grid>
              <Grid.Row>
                <Grid.Column computer={10} tablet={16} mobile={16}>
                  <Tooltip
                    trigger={
                      <Form.Input
                        name="registre"
                        label="Acte enregistré sur le registre"
                        placeholder="Sélectionner un registre"
                        value={
                          data?.registre
                            ? `Année ${data.registre.annee?.annee} - ${data.registre.libelle}`
                            : ''
                        }
                        error={!_.isNil(displayedErrors.registre)}
                        action={
                          <Button
                            type="button"
                            basic
                            icon="search"
                            onClick={() => setOpenModalRegistre(true)}
                          />
                        }
                        onChange={handleChange}
                      />
                    }
                    type={displayedErrors.registre ? 'error' : ''}
                    message={displayedErrors.registre}
                  />
                </Grid.Column>
                <Grid.Column computer={6} tablet={16} mobile={16}>
                  <Form.Dropdown
                    selection
                    required
                    name="identifiantSousTypeActe"
                    label="Sous-type d'acte"
                    placeholder="Sous-type d'acte"
                    options={sousTypeActeOptions}
                    value={data?.identifiantSousTypeActe ?? ''}
                    error={!_.isNil(displayedErrors.identifiantSousTypeActe)}
                    onChange={handleSousTypeChange}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column computer={4} tablet={8} mobile={16}>
                  <Form.Field error={!_.isNil(displayedErrors.dateDresseActe)}>
                    <label>Dressé le</label>
                    <Tooltip
                      trigger={
                        <DateInput
                          value={
                            data?.dateDresseActe
                              ? dayjs(data.dateDresseActe).toDate()
                              : undefined
                          }
                          onChange={handleDateChange}
                        />
                      }
                      type={displayedErrors.dateDresseActe ? 'error' : ''}
                      message={displayedErrors.dateDresseActe}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column computer={3} tablet={8} mobile={16}>
                  <Form.Group style={{ alignItems: 'flex-end' }}>
                    <Form.Input
                      label="à"
                      placeholder="hh"
                      name="heureDresseActe"
                      style={{ width: 48 }}
                      value={
                        !_.isNil(data?.heureDresseActe)
                          ? String(data.heureDresseActe).padStart(2, '0')
                          : ''
                      }
                      onChange={handleTimeInput}
                    />
                    <span style={{ alignSelf: 'flex-end', lineHeight: '38px' }}>
                      :
                    </span>
                    <Form.Input
                      placeholder="mm"
                      name="minuteDresseActe"
                      style={{ width: 48 }}
                      value={
                        !_.isNil(data?.minuteDresseActe)
                          ? String(data.minuteDresseActe).padStart(2, '0')
                          : ''
                      }
                      onChange={handleTimeInput}
                    />
                  </Form.Group>
                </Grid.Column>
                <Grid.Column computer={3} tablet={8} mobile={16}>
                  <Tooltip
                    trigger={
                      <Form.Input
                        label="Acte N°"
                        name="numeroActe"
                        error={!_.isNil(displayedErrors.numeroActe)}
                        value={data?.numeroActe ?? ''}
                        onChange={handleChange}
                      />
                    }
                    type={displayedErrors.numeroActe ? 'error' : ''}
                    message={displayedErrors.numeroActe}
                  />
                </Grid.Column>
                <Grid.Column computer={3} tablet={8} mobile={16}>
                  <Form.Input
                    label="Ordre N°"
                    name="numeroOrdre"
                    value={data?.numeroOrdre ?? ''}
                    onChange={handleChange}
                  />
                </Grid.Column>
                <Grid.Column computer={3} tablet={8} mobile={16}>
                  <Form.Input
                    label="Complément n°"
                    name="acteNumeroComplement"
                    value={data?.acteNumeroComplement ?? ''}
                    onChange={handleChange}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
            {champsOfficier && (
              <>
                {/* @ts-ignore */}
                <SectionHeader title="Officier d'état civil" required={false} />
                <Grid>
                  <Grid.Row style={{ paddingLeft: '1em' }}>
                    <Grid.Column computer={5} tablet={8} mobile={16}>
                      <Form.Input
                        fluid
                        label="Nom de famille"
                        name="acteSignataireNomFamille"
                        // ec_actm.am_a_sign_nom_famille est un varchar(50).
                        maxLength={50}
                        value={data?.acteSignataireNomFamille ?? ''}
                        onChange={handleChange}
                      />
                    </Grid.Column>
                    <Grid.Column computer={5} tablet={8} mobile={16}>
                      <Form.Input
                        fluid
                        label="Prénom"
                        name="acteSignatairePrenoms"
                        maxLength={100}
                        value={data?.acteSignatairePrenoms ?? ''}
                        onChange={handleChange}
                      />
                    </Grid.Column>
                    <Grid.Column computer={6} tablet={16} mobile={16}>
                      <Form.Input
                        fluid
                        label="Grade signataire"
                        name="acteSignataireQualite"
                        maxLength={100}
                        value={data?.acteSignataireQualite ?? ''}
                        onChange={handleChange}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>
            <Icon name="close" />
            Annuler
          </Button>
          <Button
            positive
            loading={saving}
            disabled={saving}
            onClick={handleSave}
          >
            <Icon name="check" />
            Enregistrer
          </Button>
        </Modal.Actions>
      </Modal>
      <ModalRegistreTreeView
        open={openModalRegistre}
        typeRegistre={typeRegistre}
        onClose={() => setOpenModalRegistre(false)}
        onSelect={handleSelectRegistre}
      />
    </>
  );
};

export default ModalRegistreActe;
